import { currentContext, safe, state } from './config'
import { DIALOGS, elementOf, notifyEffect, resolveLabel } from './interactions'
import { isActive } from './journey'
import { cleanStr, maskText, toKey } from './scrub'
import { record } from './transport'

const SKIPPED_TYPES = new Set(['hidden', 'submit', 'button', 'reset', 'image'])
const SEARCH_NAME = /^(?:search|q|query|keywords?)$/i
const FILTER_INPUT_MS = 3000
const VALIDITY_FLAGS = [
  'valueMissing',
  'typeMismatch',
  'patternMismatch',
  'tooLong',
  'tooShort',
  'rangeUnderflow',
  'rangeOverflow',
  'stepMismatch',
  'badInput',
  'customError',
]

const names = new WeakMap()

let forms = new Map()
let touchedOnPage = new Set()
let invalid = null
let lastEdit = null

function isField(el) {
  if (!el) {
    return false
  }

  if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
    return true
  }

  return el.tagName === 'INPUT' && !SKIPPED_TYPES.has(el.type)
}

function isSearchField(el) {
  return el.type === 'search' || SEARCH_NAME.test(el.getAttribute('name') ?? '')
}

// Search and GET filter forms only change what is listed; leaving them is not abandoning work.
function isSearchForm(form) {
  return form.closest('[role=search]') !== null || form.getAttribute('method')?.toLowerCase() === 'get'
}

// Field identity only; values are never read.
function fieldName(el) {
  if (!names.has(el)) {
    names.set(
      el,
      cleanStr(
        el.getAttribute('name') ||
          el.id ||
          el.getAttribute('aria-label') ||
          el.labels?.[0]?.textContent ||
          el.closest('[data-slot=field]')?.querySelector('[data-slot=field-label]')?.textContent,
        64
      )
    )
  }

  return names.get(el)
}

function formState(form) {
  let entry = forms.get(form)

  if (!entry) {
    releaseForms(null)
    entry = {
      key: `${state.page?.component ?? 'unknown'}#${Array.prototype.indexOf.call(document.forms, form)}`,
      engagedAt: null,
      lastInputAt: null,
      touched: new Set(),
      filled: false,
      lastField: null,
      inDialog: !!form.closest(DIALOGS),
      search: isSearchForm(form),
      awaitingAnswer: false,
      succeeded: false,
      serverErrors: false,
    }
    forms.set(form, entry)
  }

  return entry
}

function engage(form, field) {
  const entry = formState(form)

  if (entry.engagedAt === null) {
    entry.engagedAt = Date.now()
    record('form.engage', { target: entry.key, props: { field: fieldName(field), in_dialog: entry.inDialog } })
  }

  return entry
}

function onFocusIn(event) {
  if (!isActive()) {
    return
  }

  const field = elementOf(event.target)

  if (!isField(field)) {
    return
  }

  notifyEffect('focus')

  if (field.form) {
    engage(field.form, field)
  }
}

function onInput(event) {
  if (!isActive()) {
    return
  }

  const field = elementOf(event.target)

  if (!isField(field) || !field.form) {
    return
  }

  const entry = engage(field.form, field)
  const name = fieldName(field)
  const now = Date.now()

  entry.lastInputAt = now
  lastEdit = { form: field.form, at: now }

  if (name) {
    entry.touched.add(name)
    entry.filled = entry.filled || !isSearchField(field)
    entry.lastField = name
    touchedOnPage.add(name)
  }
}

function onSubmit(event) {
  const form = event.target

  if (!isActive() || !(form instanceof HTMLFormElement)) {
    return
  }

  const entry = formState(form)
  const fields = Array.from(form.elements).filter(isField)

  entry.awaitingAnswer = true
  const label = event.submitter && !currentContext().masked ? maskText(resolveLabel(event.submitter), 60) : null
  const recorded = record('form.submit', {
    target: entry.key,
    props: {
      submit_label: label,
      fields_touched: entry.touched.size,
      field_count: fields.length,
      native_invalid: fields.filter((field) => field.willValidate && !field.validity.valid).length,
      has_file: fields.some((field) => field.type === 'file' && field.files?.length > 0),
      in_dialog: entry.inDialog,
    },
  })

  if (recorded) {
    state.lastTrigger = { seq: recorded.seq, label, at: Date.now(), form, submitted: true }
  }

  // A submit no handler prevented is a native submission: the browser leaves the page once the server answers.
  window.setTimeout(
    safe(() => {
      if (!event.defaultPrevented) {
        notifyEffect('navigation')
      }
    }),
    0
  )
}

function emitInvalid() {
  const batch = invalid

  invalid = null

  if (batch && Object.keys(batch.fields).length > 0) {
    record('form.invalid', { target: formState(batch.form).key, props: { fields: batch.fields } })
  }
}

// The browser fires one invalid event per field in a loop; they are batched into one event per blocked submit.
function onInvalid(event) {
  if (!isActive()) {
    return
  }

  const field = elementOf(event.target)

  if (!isField(field) || !field.form) {
    return
  }

  if (invalid && invalid.form !== field.form) {
    emitInvalid()
  }

  if (!invalid) {
    invalid = { form: field.form, fields: {} }
    window.setTimeout(safe(emitInvalid), 0)
  }

  const key = toKey(fieldName(field))
  const flag = VALIDITY_FLAGS.find((name) => field.validity?.[name])

  if (key && flag && Object.keys(invalid.fields).length < 20) {
    invalid.fields[key] = flag
  }
}

export function touchedCount() {
  return touchedOnPage.size
}

// Only a request the person started succeeds a form: the form they submitted or clicked in, or every engaged form
// when they acted outside any form (a dialog footer). Background posts (timers, uploads) have no trigger.
function triggeredForms(trigger, mark) {
  if (!trigger) {
    return
  }

  forms.forEach((entry, form) => {
    if (entry.engagedAt !== null && (!trigger.form || trigger.form === form)) {
      mark(entry)
    }
  })
}

export function markSubmitStarted(trigger) {
  triggeredForms(trigger, (entry) => {
    entry.awaitingAnswer = true
  })
}

export function markSubmitSucceeded(trigger) {
  triggeredForms(trigger, (entry) => {
    entry.succeeded = true
    entry.awaitingAnswer = false
  })
}

// Filter visits are often debounced from typing; with no click or submit behind one, the form typed in last ran it.
export function markFilterSucceeded(trigger, startedAt) {
  const typedIn = lastEdit && startedAt - lastEdit.at <= FILTER_INPUT_MS ? lastEdit.form : null
  const entry = forms.get(trigger ? trigger.form : typedIn)

  if (entry) {
    entry.succeeded = true
  }
}

export function markServerErrors() {
  forms.forEach((entry) => {
    if (entry.engagedAt !== null) {
      entry.serverErrors = true
      entry.awaitingAnswer = false
    }
  })
}

export function resetForms() {
  forms = new Map()
  touchedOnPage = new Set()
  lastEdit = null
}

// Only forms the person actually typed into count as abandoned; an autofocused field or a search box alone does not.
// The time spent ends at the last input, not when the page is left (a cancelled dialog form can sit for minutes).
// A submit still waiting for its answer is not abandoned: a native submit leaves the page before success is seen.
function recordAbandon(entry) {
  if (entry.succeeded || entry.awaitingAnswer || entry.search || !entry.filled) {
    return
  }

  record('form.abandon', {
    target: entry.key,
    duration_ms: Math.max(entry.lastInputAt - entry.engagedAt, 0),
    props: {
      fields_touched: [...new Set([...entry.touched].map(toKey).filter(Boolean))].slice(0, 20),
      last_field: entry.lastField,
      had_server_errors: entry.serverErrors,
      in_dialog: entry.inDialog,
    },
  })
}

export function checkAbandon() {
  forms.forEach(recordAbandon)
  resetForms()
}

// Forms in a closed dialog (or no longer in the page) are settled now instead of at page leave, which also lets their
// detached DOM go. One whose submit is still waiting for its answer waits for page leave: its request may still answer.
export function releaseForms(container) {
  forms.forEach((entry, form) => {
    const gone = form.isConnected === false || (container instanceof Element && container.contains(form))

    if (!gone || entry.awaitingAnswer) {
      return
    }

    recordAbandon(entry)
    forms.delete(form)

    if (lastEdit?.form === form) {
      lastEdit = null
    }
  })
}

export function installForms() {
  const options = { capture: true, passive: true }

  document.addEventListener('focusin', safe(onFocusIn), options)
  document.addEventListener('input', safe(onInput), options)
  document.addEventListener('change', safe(onInput), options)
  document.addEventListener('submit', safe(onSubmit), options)
  document.addEventListener('invalid', safe(onInvalid), options)
}
